import { useEffect, useState } from 'react';

import { API_ACCOUNT_DEVICE, VIEW_LINK } from '../../common/EsConstants';
import { esDayMonYear } from '../../common/EsDate';
import { esDelete, esRead } from '../../common/EsFetch';
import { EsEmptyState } from '../../common/EsEmptyState';
import { useEsStrings } from '../../common/EsStrings';

/** How a device came by its token, in a reader's words. */
const viaText = ( pVia, t ) => ( {
    PKCE: t( 'account-devices-via-browser', 'Signed in through a browser' ),
    DEVICE: t( 'account-devices-via-code', 'Signed in with a code' ),
    STEAM: t( 'account-devices-via-steam', 'Signed in with Steam' ),
}[ pVia ] ?? null );

/* The account page's Devices tab (es-unreal-plugin.md §4.2, §9): every game signed in as this
   account, and a sign-out for each that stops it on its next request. The list is esback's
   live tokens, newest first; a row never carries the token or its hash. Drawn while
   rolloutEsUnrealPlugin is on.

   The list is read when the tab OPENS, not when the page mounts: EsTabs keeps every panel
   mounted, and revoking a game client on the Game clients tab signs its devices out, so a
   list read at mount would still show them. `active` is whether this tab is the open one. */
export default function EsAccountDevices( { active = true } ) {
    const t = useEsStrings();
    const [ devices, setDevices ] = useState( null );
    const [ confirming, setConfirming ] = useState( null );
    const [ signedOut, setSignedOut ] = useState( null );
    const [ error, setError ] = useState( null );
    const [ busy, setBusy ] = useState( false );

    const load = () => esRead( API_ACCOUNT_DEVICE )
        .then( ( pList ) => setDevices( pList ?? [] ) )
        .catch( ( error_ ) => {
            setDevices( [] );
            setError( error_?.message ?? t( 'account-devices-error', 'That did not work. Try again.' ) );
        } );

    // Each time the tab opens, for the reason above. load() reports its own failure and never rejects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect( () => { if ( active ) void load(); }, [ active ] );

    const signOut = async ( pDevice ) => {
        setBusy( true );
        setError( null );
        try {
            await esDelete( API_ACCOUNT_DEVICE + '/' + pDevice.id );
            setSignedOut( pDevice.name );
            setConfirming( null );
            await load();
        }
        catch ( error_ ) {
            setError( error_?.message ?? t( 'account-devices-error', 'That did not work. Try again.' ) );
        }
        finally {
            setBusy( false );
        }
    };

    if ( devices === null ) return null;

    return (
        <div data-testid="es-account-devices">
            { /* The tab's one action at the right of its one sentence, a button rather than a link
                 run into the prose, where it wrapped mid-phrase (Tim, 2026-10-22). */ }
            <div className="mb-6 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
                <p className="mb-0 text-sm text-aside">
                    { t( 'account-devices-blurb', 'Games signed in as you. Sign one out and it has to sign in again.' ) }
                </p>
                <a href={ VIEW_LINK } data-testid="es-account-devices-link" className="buttonSecondary mx-0 shrink-0 px-4 py-1.5">
                    { t( 'account-devices-add', 'Sign in a device' ) }
                </a>
            </div>

            { signedOut &&
                <output data-testid="es-account-devices-signed-out" className="formStatus mb-4 block">
                    { t( 'account-devices-signed-out', '{name} is signed out.', { name: signedOut } ) }
                </output> }

            { devices.length === 0
                ? <EsEmptyState icon="pi-desktop" data-testid="es-account-devices-empty">
                    { t( 'account-devices-empty', 'No devices are signed in.' ) }
                </EsEmptyState>
                : <ul data-testid="es-account-devices-list" className="divide-y divide-field-edge">
                    { devices.map( ( pDevice ) => (
                        <li key={ pDevice.id } data-testid={ `es-account-devices-row-${ pDevice.id }` }
                            className="flex flex-wrap items-center justify-between gap-3 py-4">
                            <div className="min-w-0">
                                <p data-testid={ `es-account-devices-row-${ pDevice.id }-name` } className="font-medium text-heading">
                                    { pDevice.name }
                                </p>
                                <p className="text-xs text-aside">
                                    { [ pDevice.platform, viaText( pDevice.via, t ) ].filter( Boolean ).join( ' · ' ) }
                                </p>
                                <p className="text-xs text-aside">
                                    { t( 'account-devices-signed-in', 'Signed in {date}', { date: esDayMonYear( pDevice.signedIn ) } ) }
                                    { ' · ' }
                                    { pDevice.lastUsed
                                        ? t( 'account-devices-last-used', 'Last used {date}', { date: esDayMonYear( pDevice.lastUsed ) } )
                                        : t( 'account-devices-never-used', 'Not used yet' ) }
                                    { ' · ' }
                                    { t( 'account-devices-expires', 'Expires {date}', { date: esDayMonYear( pDevice.expires ) } ) }
                                </p>
                            </div>
                            { confirming === pDevice.id
                                ? <div className="flex gap-2">
                                    <button type="button" disabled={ busy } onClick={ () => signOut( pDevice ) }
                                            data-testid={ `es-account-devices-row-${ pDevice.id }-confirm` }
                                            className="rounded-sm px-2 py-1 text-sm font-bold text-danger ring-focus focus:ring-2 disabled:opacity-50">
                                        { t( 'account-devices-sign-out-confirm', 'Sign it out' ) }
                                    </button>
                                    <button type="button" disabled={ busy } onClick={ () => setConfirming( null ) }
                                            data-testid={ `es-account-devices-row-${ pDevice.id }-cancel` }
                                            className="rounded-sm px-2 py-1 text-sm text-body ring-focus focus:ring-2 disabled:opacity-50">
                                        { t( 'account-devices-keep', 'Keep it' ) }
                                    </button>
                                </div>
                                : <button type="button" disabled={ busy } onClick={ () => setConfirming( pDevice.id ) }
                                          data-testid={ `es-account-devices-row-${ pDevice.id }-sign-out` }
                                          className="rounded-sm px-2 py-1 text-sm text-body underline ring-focus focus:ring-2 disabled:opacity-50">
                                    { t( 'account-devices-sign-out', 'Sign out' ) }
                                </button> }
                        </li>
                    ) ) }
                </ul> }

            { error && <p data-testid="es-account-devices-error" role="alert" className="formError mt-4"> { error } </p> }
        </div>
    );
}
